import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Zap, Search } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Property, GalleryImage } from '../types/property';
import { Sidebar } from './components/sidebar';
import { PropertyCard } from './components/property-card';
import { PropertyDetail } from './components/property-detail';
import { AllPhotosView } from './components/all-photos-view';
import { TrashView } from './components/trash-view';
import { Settings } from './components/settings';
import { RenderWizard } from './components/render-wizard';
import { RenderPreview } from './components/render-preview';
import { NewPropertyModal } from './components/new-property-modal';
import { NewPropertyPlaceholder } from './components/new-property-placeholder';
import { DeletePropertyDialog } from './components/delete-property-dialog';
import { PageHeader } from './components/page-header';
import { GlobalLoader } from './components/global-loader';
import { MobileNav } from './components/mobile-nav';
import { MobileMenu } from './components/mobile-menu';
import { MobileHeader } from './components/mobile-header';
import { AdminDashboard } from './components/admin/AdminDashboard';

import { useProperties } from './hooks/useProperties';
import { usePhotos } from './hooks/usePhotos';
import { useRenderWorkflow } from './hooks/useRenderWorkflow';
import { useGenerationState } from './hooks/useGenerationState';
import { useDeletePropertyDialog } from './hooks/useDeletePropertyDialog';
import { useRenderGenerate } from './hooks/useRenderGenerate';
import { usePhotoClick } from './hooks/usePhotoClick';
import { useRenderPreviewActions } from './hooks/useRenderPreviewActions';

// Type re-export for component props compatibility
export type { Property, GalleryImage, RenderSettings } from '../types/property';

// Generate all photos across all properties
const generateAllPhotos = (propertiesList: Property[], includeDeleted: boolean = false) => {
  let all: Array<{
    id: string;
    url: string;
    category: any;
    propertyAddress: string;
    date: string;
    aspectRatio: 'landscape' | 'portrait' | 'square';
    roomName?: string;
    favorite: boolean;
    deleted: boolean;
    image: GalleryImage;
  }> = [];

  propertiesList.forEach(prop => {
    if (prop.galleryImages) {
      prop.galleryImages.forEach(img => {
        if (includeDeleted || !prop.deleted) {
          if (includeDeleted && !img.deleted) return;
          if (!includeDeleted && img.deleted) return;

          all.push({
            id: img.id,
            url: img.url,
            category: img.category,
            aspectRatio: img.aspectRatio || 'landscape',
            propertyAddress: prop.address,
            date: prop.date,
            roomName: img.roomName,
            favorite: img.favorite,
            deleted: img.deleted || false,
            image: img
          });
        }
      });
    }
  });

  return all;
};

export default function App() {
  const { user, profile, refreshProfile, isAdmin } = useAuth();

  // Custom Hooks
  const {
    properties,
    setProperties,
    fetchProperties,
    createProperty,
    renameProperty,
    deleteProperty,
    imagesLoading
  } = useProperties();

  const {
    handleDeletePhotoFromGallery,
    handleRestorePhoto,
    handleToggleFavorite,
    handleMovePhoto,
    handleAssignUnsortedPhoto
  } = usePhotos(properties, setProperties, user?.id);

  const {
    isRenderWizardOpen,
    setIsRenderWizardOpen,
    isRenderPreviewOpen,
    setIsRenderPreviewOpen,
    renderWizardState,
    setRenderWizardState,
    selectedPhotoData,
    setSelectedPhotoData
  } = useRenderWorkflow();

  const { isGenerating, generatingPropertyId, generatingImageId, activeGenerationId, startGenerating, stopGenerating, setActiveGenerationId } = useGenerationState();

  // Local UI State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState<'properties' | 'allphotos' | 'favorites' | 'trash' | 'settings' | 'admin'>('properties');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const deleteDialog = useDeletePropertyDialog(deleteProperty);

  const { handleRenderGenerate } = useRenderGenerate({
    selectedProperty,
    setSelectedProperty,
    setProperties,
    selectedPhotoData,
    setSelectedPhotoData,
    setIsRenderWizardOpen,
    setIsRenderPreviewOpen,
    stopGenerating,
    refreshProfile,
    activeGenerationId,
    setActiveGenerationId,
  });

  const { handleAllPhotosClick, handlePropertyPhotoClick } = usePhotoClick({
    properties,
    setSelectedProperty,
    setSelectedPhotoData,
    setIsRenderPreviewOpen,
  });

  const previewActions = useRenderPreviewActions({
    selectedProperty,
    setSelectedProperty,
    selectedPhotoData,
    setSelectedPhotoData,
    properties,
    setProperties,
    setIsRenderPreviewOpen,
    setIsRenderWizardOpen,
    setRenderWizardState,
    handleDeletePhotoFromGallery,
    handleToggleFavorite,
    handleMovePhoto,
    handleAssignUnsortedPhoto,
    createProperty,
    fetchProperties,
  });

  // Handle deep-link query params (e.g. ?tab=pricing from emails)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'pricing') {
      setCurrentView('settings');
      setSearchParams({}, { replace: true });
    }
  }, []);

  const allPhotos = useMemo(() => generateAllPhotos(properties), [properties]);
  const trashedPhotos = useMemo(() => generateAllPhotos(properties, true), [properties]);

  // Show global loader during initial data fetch
  // MUST be placed after all hooks to avoid "Rendered fewer hooks than expected" error
  if (!user || (user && !profile)) {
    return <GlobalLoader text="Wysko ładuje profil..." />;
  }

  if (imagesLoading) {
    return <GlobalLoader text="Wysko przygotowuje pliki..." />;
  }

  const handleCreatePropertyWrapper = (name: string) => {
    createProperty(name);
    setIsModalOpen(false);
  };

  const handleNavigation = (nav: string) => {
    if (['properties', 'allphotos', 'favorites', 'trash', 'settings', 'admin'].includes(nav)) {
      setCurrentView(nav as any);
      if (nav !== 'properties') setSelectedProperty(null);
    }
  };

  const handleGoToProperty = (propertyAddress: string) => {
    const prop = properties.find(p => p.address === propertyAddress);
    if (prop) {
      setSelectedProperty(prop);
      setCurrentView('properties');
    }
  };

  const filteredProperties = properties
    .filter(p => !p.deleted)
    .filter(property =>
      property.address.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const getActiveNav = () => {
    if (selectedProperty) return 'properties';
    return currentView;
  };

  return (
    <div className="h-screen overflow-hidden bg-background relative">
      <Toaster />

      {/* Banner dla kont zaplanowanych do usunięcia */}
      {profile?.scheduled_deletion_at && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-3 relative z-50">
          <p className="text-[13px] text-amber-800 dark:text-amber-200 text-center">
            Twoje konto jest w fazie usuwania. Jeżeli uważasz, że to błąd lub chciałbyś
            przywrócić swoje konto, skontaktuj się z{' '}
            <a href="mailto:kontakt@wysko.pl" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">
              kontakt@wysko.pl
            </a>
          </p>
        </div>
      )}

      <div className="bg-grid-pattern" />

      <Sidebar activeNav={getActiveNav()} onNavigate={handleNavigation} />
      <MobileHeader
        title={selectedProperty ? selectedProperty.address : currentView === 'allphotos' ? 'Wszystkie pliki' : currentView === 'favorites' ? 'Ulubione' : currentView === 'trash' ? 'Kosz' : currentView === 'settings' ? 'Ustawienia' : 'Moje nieruchomości'}
        showBack={!!selectedProperty}
        onBackClick={() => setSelectedProperty(null)}
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />
      <MobileMenu
        open={isMobileMenuOpen}
        onOpenChange={setIsMobileMenuOpen}
        activeNav={getActiveNav()}
        onNavigate={handleNavigation}
        onQuickRender={() => setIsRenderWizardOpen(true)}
      />
      <MobileNav activeNav={getActiveNav()} onNavigate={handleNavigation} onQuickRender={() => setIsRenderWizardOpen(true)} />

      <main className="lg:ml-[340px] h-screen overflow-hidden flex flex-col pb-16 lg:pb-0">
        {currentView !== 'admin' && <div className="h-14 lg:h-[180px] flex-shrink-0" />}

        <div className="flex-1 overflow-y-auto px-4 lg:px-5 pb-5">
          {selectedProperty ? (
            <PropertyDetail
              property={properties.find(p => p.id === selectedProperty.id) || selectedProperty}
              onBack={() => setSelectedProperty(null)}
              onPhotoClick={handlePropertyPhotoClick}
              onAddPhotoClick={() => {
                setRenderWizardState({ mode: 'create' });
                setIsRenderWizardOpen(true);
              }}
              onAddressUpdate={(newAddress) => {
                renameProperty(selectedProperty.id, newAddress);
              }}
              onDeletePhoto={(photoUrl) => {
                handleDeletePhotoFromGallery(selectedProperty.id, photoUrl);
              }}
              onToggleFavorite={handleToggleFavorite}
              isGenerating={isGenerating}
              generatingPropertyId={generatingPropertyId}
              generatingImageId={generatingImageId}
            />
          ) : currentView === 'allphotos' ? (
            <AllPhotosView
              allPhotos={allPhotos}
              onPhotoClick={handleAllPhotosClick}
              onGoToProperty={handleGoToProperty}
              onDeletePhoto={(photoUrl) => {
                const photo = allPhotos.find(p => p.url === photoUrl);
                if (photo) {
                  const property = properties.find(p => p.address === photo.propertyAddress);
                  if (property) {
                    handleDeletePhotoFromGallery(property.id, photoUrl);
                  }
                }
              }}
              onToggleFavorite={handleToggleFavorite}
            />
          ) : currentView === 'favorites' ? (
            <AllPhotosView
              title="Ulubione"
              allPhotos={allPhotos.filter(p => p.favorite)}
              onPhotoClick={handleAllPhotosClick}
              onGoToProperty={handleGoToProperty}
              onDeletePhoto={(photoUrl) => {
                const photo = allPhotos.find(p => p.url === photoUrl);
                if (photo) {
                  const property = properties.find(p => p.address === photo.propertyAddress);
                  if (property) {
                    handleDeletePhotoFromGallery(property.id, photoUrl);
                  }
                }
              }}
              onToggleFavorite={handleToggleFavorite}
            />
          ) : currentView === 'trash' ? (
            <TrashView
              trashedPhotos={trashedPhotos}
              onRestore={handleRestorePhoto}
            />
          ) : currentView === 'settings' ? (
            <Settings
              onOpenAdmin={isAdmin ? () => setCurrentView('admin') : undefined}
            />
          ) : currentView === 'admin' ? (
            <AdminDashboard onBack={() => setCurrentView('settings')} />
          ) : (
            <div className="min-h-full relative isolate">
              <PageHeader
                title="Moje nieruchomości"
                actions={
                  <>
                    <Button
                      variant="default"
                      size="lg"
                      className="gap-2"
                      onClick={() => setIsRenderWizardOpen(true)}
                    >
                      <Zap className="w-4 h-4" strokeWidth={2} />
                      Szybki Render
                    </Button>
                  </>
                }
                controls={
                  <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
                    <Input
                      type="text"
                      placeholder="Szukaj..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-8 text-[13px] bg-background border-border"
                    />
                  </div>
                }
              />

              <div className="px-4 lg:px-5 py-6 lg:py-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-4 lg:gap-6">
                  <NewPropertyPlaceholder
                    onCreateProperty={(name) => {
                      handleCreatePropertyWrapper(name);
                    }}
                  />

                  {filteredProperties.map((property) => {
                    const validImages = property.galleryImages?.filter(img => !img.deleted) || [];
                    const displayImageUrl = validImages.length > 0 ? (validImages[0].variants?.[0]?.url || validImages[0].url) : property.imageUrl;
                    const thumbnails = validImages.slice(1, 4).map(img => img.variants?.[0]?.url || img.url);

                    return (
                      <PropertyCard
                        key={property.id}
                        address={property.address}
                        photoCount={validImages.length}
                        date={property.date}
                        imageUrl={displayImageUrl}
                        thumbnails={thumbnails}
                        onCardClick={() => setSelectedProperty(property)}
                        onRename={(newAddress) => renameProperty(property.id, newAddress)}
                        onDeleteClick={() => deleteDialog.openDialog(property)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <NewPropertyModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onCreateProperty={handleCreatePropertyWrapper}
      />

      <RenderWizard
        open={isRenderWizardOpen}
        onOpenChange={setIsRenderWizardOpen}
        onRenderGenerate={handleRenderGenerate}
        onGenerationStart={() => {
          startGenerating(selectedProperty?.id || null, renderWizardState.initialImageId || null);
          toast.loading('Trwa przygotowywanie Twojego projektu. To może chwilę potrwać...', {
            id: 'generation-process',
            duration: 60000,
          });
        }}
        onGenerationError={(error) => {
          stopGenerating();
          toast.dismiss('generation-process');
          console.error('Generation error:', error);
        }}
        initialImageUrl={renderWizardState.initialImageUrl}
        initialImageId={renderWizardState.initialImageId}
        initialRoomName={renderWizardState.initialRoomName}
        initialGcsPath={renderWizardState.initialGcsPath}
        userId={user?.id}
        propertyId={selectedProperty?.id}
      />

      <RenderPreview
        open={isRenderPreviewOpen}
        onOpenChange={setIsRenderPreviewOpen}
        propertyAddress={selectedProperty?.address || ""}
        roomName={selectedPhotoData?.roomName || "Salon"}
        variants={selectedPhotoData?.variants || []}
        originalUrl={selectedPhotoData?.originalUrl}
        renderSettings={selectedPhotoData?.renderSettings || { mode: 'enhance' }}
        onDelete={previewActions.handleDelete}
        isFavorite={previewActions.isFavorite}
        onToggleFavorite={previewActions.handleToggleFav}
        availableProperties={previewActions.availableProperties}
        currentPropertyId={selectedProperty?.id}
        onMove={previewActions.handleMove}
        onAutoSaveAndClose={previewActions.handleAutoSaveAndClose}
        onRerender={previewActions.handleRerender}
        generationId={selectedPhotoData?.generationId}
        onRetryVariant={previewActions.handleRetryVariant}
        isRetryingVariant={previewActions.isRetrying}
      />

      <DeletePropertyDialog
        open={deleteDialog.isOpen}
        onOpenChange={deleteDialog.setIsOpen}
        propertyAddress={deleteDialog.propertyAddress}
        onConfirm={deleteDialog.confirmDelete}
      />
    </div>
  );
}
